import { initializeApp } from 'firebase/app';
import { initializeAnalytics, logEvent, isSupported, setAnalyticsCollectionEnabled, setUserProperties } from 'firebase/analytics';

import {
  INTERNAL_USER_CHANGED_EVENT,
  INTERNAL_USER_PROPERTY,
  resolveInternalUser,
  toInternalUserPropertyValue,
} from '@/shared/lib/analytics/internal-user';
import { CONSENT_CHANGED_EVENT, isTrackingAllowed } from '@/shared/lib/cookie-consent';

import type { FirebaseApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
};

/**
 * 🔴 TRAD-1155 — **동의 전에는 `initializeApp` 도 `getAnalytics` 도 부르지 않는다.**
 *
 * 종전엔 이 파일이 import 되는 순간(모듈 로드 시점) 둘 다 즉시 실행됐다. 2026-09-10 운영
 * 실측(fresh context, 동의 절차 없음)에서 그 결과가 이렇게 나왔다:
 *   www.googletagmanager.com · firebase.googleapis.com
 *   firebaseinstallations.googleapis.com · www.google-analytics.com
 *
 * 🔴 **배너를 띄우는 것만으로는 아무것도 안 고친 것이다** — 뒤에서 이미 돌기 때문이다. 그래서
 * 초기화 자체를 동의 뒤로 미룬다.
 *
 * ※ `index.html` 에는 GA/GTM 직접 로드가 없다(실측: `grep -nE "googletagmanager|gtag|…"
 *   index.html` → 0건). 위 `googletagmanager` 요청은 **Firebase Analytics SDK 가 gtag.js 를
 *   끌어오는 것**이므로, 이 한 곳을 막으면 네 개가 같이 사라진다. 막을 곳이 늘지 않는다.
 */
let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let initializing = false;

/**
 * TRAD-3792 — 초기화 전에 들어온 page_view 를 «가장 최근 1건만» 보류한다(전송하지 않는다).
 *
 * 초기화는 `isSupported()` 를 비동기로 기다린 뒤 끝나므로, 라우트 트래커의 첫 page_view 가 그보다 먼저 오면 버려졌다.
 * 종전엔 gtag 자동 page_view(send_page_view 기본값)가 그 빈자리를 우연히 채웠다 — 세션 중에 동의한 랜딩 페이지가 대표적이다
 * (dev 2026-10-22 실측: 동의 직후 page_view 는 자동 1건뿐, 수동은 다음 라우트부터). 자동을 끈 뒤로는 이 보류가 그 1건을 맡는다.
 * 🔴 보류는 메모리에만 있고, **추적이 허용돼 초기화가 끝났을 때만** 나간다. EU 미동의면 초기화가 안 되므로 영원히 안 나간다.
 * 🔴 1건만 두는 이유: 그 사이 여러 라우트를 지났어도 «지금 보고 있는 화면»만 의미가 있다 — 쌓아 두면 동의 시점에 과거 화면이 몰려 나간다.
 */
type PageViewArgs = { pagePath: string; pageTitle?: string; pageLocation?: string };
let pendingPageView: PageViewArgs | null = null;

const sendPageView = (a: Analytics, { pagePath, pageTitle, pageLocation }: PageViewArgs) => {
  logEvent(a, 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    // TRAD-3701 — GA4 는 page_path 가 아니라 page_location(dl)을 본다. 안 주면 SPA 이동에서 이전 URL 이 남았다(dev 실측).
    ...(pageLocation ? { page_location: pageLocation } : {}),
  });
};

const flushPendingPageView = () => {
  if (!analytics || !pendingPageView || !isTrackingAllowed()) return;
  // 🔴 보류된 값(호출 «시점»의 path·title·location)을 그대로 보낸다. 여기서 document.title·location 을 다시 읽으면
  // TRAD-3701 이 고친 «이전 화면 제목» 결함이 되살아난다.
  const pv = pendingPageView;
  pendingPageView = null;
  sendPageView(analytics, pv);
};

/**
 * 추적이 허용되면 Firebase 를 초기화한다. **허용되지 않으면 아무 일도 하지 않는다.**
 * 허용 여부는 `isTrackingAllowed()` — 저장된 선택 + 지역(TRAD-3792, `consent-region.ts`) 규칙 한 곳이다.
 * 여러 번 불려도 안전하다(이미 초기화됐거나 진행 중이면 즉시 반환).
 */
export const initAnalyticsIfConsented = (): void => {
  if (analytics || initializing) return;
  if (!isTrackingAllowed()) return;

  initializing = true;
  app = initializeApp(firebaseConfig);

  isSupported()
    .then(supported => {
      if (supported && app) {
        // TRAD-3792 — `getAnalytics` 는 gtag config 를 기본값(send_page_view=true)으로 보내 자동 page_view 를
        // 한 번 쏜다. 라우트 page_view 는 FirebaseAnalyticsTracker 가 따로 보내므로(TRAD-3701 제목·URL 보정)
        // 첫 화면에서 page_view 가 두 번 나갔다(운영 2026-10-22 실측: _s=1 자동 + _s=2 dp=/).
        // 자동 쪽을 끄고 수동 쪽 하나만 남긴다.
        analytics = initializeAnalytics(app, { config: { send_page_view: false } });
        // isSupported 를 기다리는 사이에 거부했을 수 있다 — 그 선택이 초기화에 덮이지 않게 한 번 더 맞춘다.
        if (!isTrackingAllowed()) setAnalyticsCollectionEnabled(analytics, false);
        // TRAD-3847 — 내부 트래픽 표시. 보류된 첫 page_view 보다 «먼저» 붙여야 그 이벤트도 값을 싣는다.
        // 🔴 여기서 던지면 아래 flush 가 안 돌아 랜딩 page_view 가 사라진다(작성 중 실측: 목 누락 → 보류 테스트 5개 사망).
        // 표시는 부가 정보이고 page_view 는 본 지표다 — 표시 실패가 본 지표를 지우지 않게 따로 잡는다.
        try {
          setUserProperties(analytics, { [INTERNAL_USER_PROPERTY]: toInternalUserPropertyValue(resolveInternalUser()) });
        } catch (e) {
          console.error('[firebase] failed to set internal_user property', e);
        }
        flushPendingPageView();
      }
      initializing = false;
    })
    .catch(e => {
      initializing = false;
      // raw error 를 삼키지 않는다 — 분석이 조용히 죽으면 원인을 못 찾는다.
      // 🔴 메시지는 영어다: 이 파일은 한글 하드코딩 래칫의 상한이 0이고(베이스라인 미등재
      // = 신규 취급), 그 래칫은 주석은 빼고 **문자열 리터럴의 한글 연속 구간**을 센다.
      // 개발자용 로그라 영어가 맞기도 하다(CLAUDE.md "영어로 먼저 작성").
      console.error('[firebase] failed to check Analytics support', e);
    });
};

// 이미 동의한 재방문자와 opt-out 지역(TRAD-3792) 첫 방문자는 배너 없이 바로 초기화된다.
initAnalyticsIfConsented();

/**
 * 이 창에서 선택이 바뀌었을 때.
 * - 아직 초기화 전: 허용되면 새로고침 없이 초기화한다.
 * - 이미 초기화됨(TRAD-3792 — opt-out 지역은 배너 없이 켜져 있다가 푸터 «쿠키 설정»에서 거부할 수 있다):
 *   수집을 끄거나 다시 켠다. 🔴 이미 심어진 GA 쿠키를 지우지는 않는다 — 이후 전송만 멈춘다.
 */
export const applyConsentChange = (): void => {
  if (analytics) {
    setAnalyticsCollectionEnabled(analytics, isTrackingAllowed());
    return;
  }
  initAnalyticsIfConsented();
};

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener(CONSENT_CHANGED_EVENT, () => applyConsentChange());
  // TRAD-3847 — 로그인·재발급 응답이 internalUser=true 를 남기면, 이미 초기화된 Analytics 에 다시 붙인다.
  // 초기화 전이면 할 일이 없다 — 초기화가 sticky 를 읽어 처음부터 "true" 로 붙인다.
  window.addEventListener(INTERNAL_USER_CHANGED_EVENT, () => {
    if (!analytics) return;
    try {
      setUserProperties(analytics, { [INTERNAL_USER_PROPERTY]: toInternalUserPropertyValue(resolveInternalUser()) });
    } catch (e) {
      console.error('[firebase] failed to set internal_user property', e);
    }
  });
}

/**
 * 🔴 아래 두 함수의 `if (!analytics)` 가드는 **종전부터 있던 것**이고, 그 덕분에
 * `logPageView`/`logCustomEvent` 를 쓰는 **소비자 24개 파일을 하나도 안 고쳐도 된다** —
 * 초기화가 늦춰지면 아무것도 전송하지 않는다. (TRAD-3792 부터 page_view 만은 1건을 보류했다가 허용·초기화 뒤 보낸다.)
 */
export const logPageView = (pagePath: string, pageTitle?: string, pageLocation?: string) => {
  if (!analytics) {
    // TRAD-3792 — 버리지 않고 1건 보류(위 pendingPageView). 전송은 초기화가 끝나고 허용될 때만.
    pendingPageView = { pagePath, pageTitle, pageLocation };
    return;
  }
  sendPageView(analytics, { pagePath, pageTitle, pageLocation });
};

export const logCustomEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (!analytics) return;
  logEvent(analytics, eventName, params);
};

/** 테스트 전용 — 모듈 수준 상태를 초기화한다. 운영 코드에서 부르지 말 것. */
export const __resetAnalyticsForTest = (): void => {
  app = null;
  analytics = null;
  initializing = false;
  pendingPageView = null;
};

export { analytics };
